<template>
  <v-card flat class="white vetalfa-view" :max-width="_getMaxWidth()">
    <div class="vetalfa-global-header" ref="screenHeader">
      <vetalfa-small-header
        showBreadCrumbAndTitle
        :title="_getLabel('MY_DATA')"
        :breadcrumb="_getLabel('MY_DATA')"
      ></vetalfa-small-header>
    </div>
    <div ref="screenContent">
      <v-row
        no-gutters
        class="px-4 pt-2"
        v-for="{ field, label, value, disabled } in editFields"
        :key="field"
      >
        <v-col cols="12">
          <p class="font-weight-bold subtitle-2 mb-1 mt-3">
            {{ label }}
          </p>
        </v-col>
        <v-col cols="12" class="">
          <v-card
            height="40px"
            elevation="0"
            class="px-2 grey lighten-4"
            :class="{ 'grey--text': disabled, 'vetalfa-pointer': !disabled }"
            @click="edit(field)"
          >
            <v-row
              no-gutters
              justify="space-between"
              align="center"
              class="vetalfa-row"
            >
              <v-col cols="11" class="ma-0 pa-0">
                <span class="subtitle-2 vetalfa-one-line-clamp">{{
                  value
                }}</span>
              </v-col>
              <v-col cols="1" class="ma-0 pa-0 pl-2">
                <v-icon
                  v-if="!disabled"
                  color="black"
                  size="16px"
                  class="vetalfa-pointer"
                >
                  {{ _icon("pencil") }}
                </v-icon>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-card v-if="isVeterinaryProfessional" outlined class="ma-4 pa-4 rounded-lg">
        <h2 class="subtitle-1 font-weight-bold mb-3">Registro profissional no MAPA</h2>
        <professional-registration-fields v-model="mapaNumber" />
        <v-btn color="primary" rounded class="text-none" :loading="savingMapRegistration" :disabled="!isMapRegistrationValid || !mapRegistrationChanged" @click="saveMapRegistration">Salvar registro MAPA</v-btn>
      </v-card>
      <v-row no-gutters class="px-4 pt-2 mb-16">
        <v-col cols="12" class="mt-8">
          <v-btn
            text
            small
            color="grey"
            class="pa-0 text-none text-decoration-underline"
            @click="startAccountExclusion"
          >
            <v-icon size="14px" color="grey">
              {{ mdiTrashCanOutline }}
            </v-icon>
            {{ _getLabel("USER_EXCLUSION") }}
          </v-btn>
          <vetalfa-dialog
            v-on:dialogClose="userExclusionModel = false"
            :title="_getLabel('USER_EXCLUSION')"
            :bottom="false"
            :toggleModel="userExclusionModel"
            :fullscreen="false"
            max-width="700px"
            :persistent="false"
            :height="_getWindowHeight() * 0.6"
          >
            <v-col cols="12" class="pa-4">
              <span v-html="_getLabel('USER_EXCLUSION_MESSAGE')"></span>
              <v-checkbox
                v-model="userExclusionConfirmation"
                :label="_getLabel('USER_EXCLUSION_CHECKBOX')"
              ></v-checkbox>
            </v-col>
            <template v-slot:actions v-if="userExclusionConfirmation">
              <v-row no-gutters class="pa-3">
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  class="text-none text-decoration-underline"
                  text
                  @click="deleteUserConfirmationAsync()"
                >
                  {{ _getLabel("USER_EXCLUSION_YES_ACTION") }}
                </v-btn>
                <v-btn
                  color="primary"
                  class="text-none"
                  @click="userExclusionModel = false"
                  rounded
                >
                  {{ _getLabel("USER_EXCLUSION_NO_ACTION") }}
                </v-btn>
              </v-row>
            </template>
          </vetalfa-dialog>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<style>
.theme--light.v-input--is-disabled.account-disabled-color input,
.theme--light.v-input--is-disabled textarea {
  color: black;
}
</style>

<script>
import { mdiTrashCanOutline } from "@mdi/js";
import { mapGetters, mapActions, mapMutations } from "vuex";
import enums from "@/constants/enums.js";
import ProfessionalRegistrationFields from '@/components/ProfessionalRegistrationFields.vue';
export default {
  name: "Account",
  components: { ProfessionalRegistrationFields },
  computed: {
    ...mapGetters("general", ["user", "isPremium", "isVeterinaryProfessional"]),
    isMapRegistrationValid() { return !this.mapaNumber || /^MV[0-9]{11}$/.test(this.mapaNumber); },
    mapRegistrationChanged() { return (this.mapaNumber || null) !== (this.user.mapaRegistrationNumber || null); },
    nationalityCountry() {
      if (!this.user) return null;
      const country = enums.COUNTRIES.find(
        (c) => c.value === this.user.nationality
      );
      return !!country ? country.text : this._getLabel("BRAZIL");
    },
    editFields() {
      return [
        {
          field: "email",
          label: this._getLabel("EMAIL_INPUT_LABEL"),
          value: this.user.email,
          disabled: true,
        },
        {
          field: "displayNamePrefix",
          label: this._getLabel("PREFIX"),
          value: this.user.displayNamePrefix,
          hideVisibility: !this.isVeterinaryProfessional,
        },
        {
          field: "displayName",
          label: this._getLabel("NAME"),
          value: this.user.displayName,
        },
        {
          field: "nationality",
          label: this._getLabel("NATIONALITY"),
          value: this.nationalityCountry,
        },
        {
          field: "cpf",
          label: this._getLabel("CPF"),
          value: this._formatCpf(this.user.cpf),
          hideVisibility: this.user.nationality != 0,
        },
        {
          field: "birthday",
          label: this._getLabel("BIRTHDAY"),
          value: this.user.birthday,
        },
        {
          field: "questions",
          label: this._getLabel("OCUPATION_AND_ACADEMIC_INFO"),
          value: this._getLabel("CLICK_TO_EDIT"),
        },
        {
          field: "address",
          label: this._getLabel("ADDRESS"),
          value: this.user.address
            ? this._formatBrazilianAddress(this.user.address)
            : "",
        },
        {
          field: "crmv",
          label: "CRMV",
          value:
            this.user.crmvState && this.user.crmvNumber
              ? `${this.user.crmvNumber}${this.user.crmvState}`
              : this._getLabel("CLICK_TO_EDIT"),
          hideVisibility: !this.isVeterinaryProfessional,
        },
      ].filter((field) => !field.hideVisibility);
    },
  },
  data() {
    return {
      userExclusionModel: false,
      userExclusionConfirmation: false,
      mdiTrashCanOutline,
      mapaNumber: '',
      savingMapRegistration: false,
    };
  },
  methods: {
    async saveMapRegistration() {
      if (!this.isMapRegistrationValid || !this.mapRegistrationChanged) return;
      this.savingMapRegistration = true;
      try {
        await this.$store.dispatch('general/saveUserData', {
          user: { mapaRegistrationNumber: this.mapaNumber || null },
          field: 'mapaRegistrationNumber', successCallback: () => {},
        });
      } finally { this.savingMapRegistration = false; }
    },
    ...mapActions("general", ["deleteUserConfirmation", "routePathRedirect"]),
    ...mapActions("purchases", ["purchasesManageSubscriptions"]),
    ...mapMutations("general", ["setConfirmModel"]),
    edit(field) {
      if (field === "email") {
        return this.setConfirmModel({
          alert: true,
          text: this._getMessage("FORBIDDEN_EMAIL_UPDATE"),
        });
      }
      this.routePathRedirect({ path: `/registration/${field}` });
    },
    startAccountExclusion() {
      if (this.isPremium) {
        this.setConfirmModel({
          active: true,
          alert: false,
          text: this._getMessage("ACCOUNT_EXCLUSION_PRO_USER"),
          callback: () => {
            this.routePathRedirect({ path: "/home" });
            this.purchasesManageSubscriptions();
          },
          cancelAction: () => {
            this.userExclusionModel = true;
          },
          confirmText: this._getLabel("CANCEL_SUBSCRIPTION"),
          closeText: this._getLabel("USER_EXCLUSION"),
        });
      } else {
        this.userExclusionModel = true;
      }
    },
    deleteUserConfirmationAsync() {
      this.userExclusionModel = false;
      this.userExclusionConfirmation = false;
      this.deleteUserConfirmation();
    },
  },
  mounted() {
    this.mapaNumber = this.user.mapaRegistrationNumber || '';
    this._setupScreenSizes(this);
  },
};
</script>
